export type SkeletonCardVariant = 'unit' | 'plan' | 'community'

interface SkeletonCardProps {
	/** Which card's footprint to mirror. @default "unit" */
	variant?: SkeletonCardVariant
	/** Reserve the icon button beside the price (UnitCard's calculator). @default false */
	hasPricingAction?: boolean
	className?: string
}

/** Placeholder bar. `line` reserves the real text row's height so the skeleton matches the card it replaces. */
function Bone ({ className = '', line = 'spaces:h-24' }: { className?: string; line?: string }) {
	return (
		<div className={`spaces:flex spaces:items-center ${line}`}>
			<div className={`spaces-skeleton-bone spaces:h-12 spaces:rounded-full spaces:bg-canvas-2 ${className}`} />
		</div>
	)
}

const imageClasses: Record<SkeletonCardVariant, string> = {
	unit: 'spaces:aspect-square',
	plan: 'spaces:aspect-square',
	community: 'spaces:aspect-video'
}

/**
 * Low-fidelity stand-in for UnitCard / PlanCard / CommunityCard while results load.
 * Mirrors each card's image ratio, details rows and pricing footer to avoid layout shift.
 */
export function SkeletonCard ({ variant = 'unit', hasPricingAction = false, className = '' }: SkeletonCardProps) {
	const cardClasses = [
		'spaces-skeleton-card spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-16 spaces:overflow-hidden spaces:flex spaces:flex-col',
		className
	]
		.filter(Boolean)
		.join(' ')

	return (
		<div className={cardClasses} aria-hidden='true'>
			{/* Image */}
			<div className={`spaces-skeleton-bone spaces:w-full spaces:bg-canvas-3 ${imageClasses[variant]}`} />

			{/* Details */}
			{variant === 'unit' && (
				<div className='spaces:flex spaces:flex-col spaces:gap-8 spaces:p-16'>
					<div className='spaces:flex spaces:flex-col'>
						<Bone className='spaces:h-16 spaces:w-1/3' />
						<Bone className='spaces:w-1/2' />
					</div>
					<Attributes withDate />
				</div>
			)}
			{variant === 'plan' && (
				<div className='spaces:flex spaces:flex-col spaces:gap-12 spaces:p-16'>
					<Bone className='spaces:h-16 spaces:w-1/2' />
					<Attributes />
					<MatchingUnits />
				</div>
			)}
			{variant === 'community' && (
				<div className='spaces:flex spaces:flex-col spaces:gap-16 spaces:p-16'>
					<div className='spaces:flex spaces:flex-col'>
						<Bone className='spaces:h-16 spaces:w-2/3' />
						<Bone className='spaces:w-1/3' />
					</div>
					<MatchingUnits />
				</div>
			)}

			{/* Pricing — the detail line stacks in narrow cards, matching CardPricing's container query */}
			<div className='spaces:mt-auto spaces:flex spaces:items-center spaces:gap-8 spaces:px-16 spaces:py-16 spaces:bg-canvas-4 spaces:border-t spaces:border-canvas-3'>
				<div className='spaces:@container spaces:flex spaces:flex-1 spaces:min-w-0 spaces:flex-col'>
					<Bone className='spaces:h-16 spaces:w-96' />
					<div className='spaces:flex spaces:flex-col spaces:gap-4 spaces:@pricing-detail:flex-row spaces:@pricing-detail:gap-8'>
						<Bone line='spaces:h-16' className='spaces:w-104' />
						<Bone line='spaces:h-16' className='spaces:w-40' />
					</div>
				</div>
				{hasPricingAction && <div className='spaces-skeleton-bone spaces:size-40 spaces:shrink-0 spaces:rounded-12 spaces:bg-canvas-3' />}
			</div>
		</div>
	)
}

/**
 * Bed / bath / area row, built on the real card's divider structure (item padding + icon slot)
 * so it wraps at the same widths. Units add the available date.
 */
function Attributes ({ withDate = false }: { withDate?: boolean }) {
	const items = withDate
		? ['spaces:w-40', 'spaces:w-48', 'spaces:w-64', 'spaces:w-80']
		: ['spaces:w-40', 'spaces:w-48', 'spaces:w-64']

	return (
		<div className='spaces:overflow-hidden'>
			<div className='spaces:-ml-13 spaces:flex spaces:flex-wrap spaces:items-center spaces:gap-y-4'>
				{items.map((width, i) => (
					<div key={i} className='spaces:flex spaces:items-center spaces:gap-4 spaces:border-l spaces:border-transparent spaces:pl-12 spaces:pr-12'>
						<div className='spaces-skeleton-bone spaces:size-20 spaces:m-2 spaces:rounded-full spaces:bg-canvas-2' />
						<Bone className={width} />
					</div>
				))}
			</div>
		</div>
	)
}

function MatchingUnits () {
	return (
		<div className='spaces:flex spaces:items-center spaces:gap-8'>
			<div className='spaces-skeleton-bone spaces:size-32 spaces:rounded-12 spaces:bg-canvas-2' />
			<Bone className='spaces:w-1/3' />
		</div>
	)
}
